import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, InputGroup, Modal } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import { BsPencilSquare, BsTrash, BsSearch, BsSortDown, BsSortUp } from 'react-icons/bs';
import { API_ENDPOINTS } from '../config'; // Import updated API endpoints

const Services = () => {
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [servicesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('providerType');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [newService, setNewService] = useState({ providerType: 'Doctor', service: '' });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.listAllServices, {
        headers: { token }
      });
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const sortServices = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredServices = services
    .filter(service => 
      service.providerType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.service.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastService = currentPage * servicesPerPage;
  const indexOfFirstService = indexOfLastService - servicesPerPage;
  const currentServices = sortedAndFilteredServices.slice(indexOfFirstService, indexOfLastService);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deleteService, {
          headers: { token, serviceId }
        });
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const handleEditService = (service) => {
    setEditingService({ ...service });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingService({
      ...editingService,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(API_ENDPOINTS.editService, editingService, {
        headers: { token, serviceId: editingService.serviceId }
      });
      setEditingService(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
    }
  };

  const handleAddServiceChange = (e) => {
    const { name, value } = e.target;
    setNewService({
      ...newService,
      [name]: value
    });
  };

  const handleAddServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(API_ENDPOINTS.addService, newService, {
        headers: { token }
      });
      setShowAddServiceModal(false);
      setNewService({ providerType: 'Doctor', service: '' });
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  return (
    <div className="d-flex">
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{  width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid className="mt-3">
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Services</h2>
                <Button variant="primary" onClick={() => setShowAddServiceModal(true)}>Add Service</Button>
              </div>
              <InputGroup className="mb-3">
                <InputGroup.Text><BsSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th onClick={() => sortServices('providerType')}>
                      Provider Type {sortField === 'providerType' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortServices('service')}>
                      Service {sortField === 'service' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentServices.map((service, index) => (
                    <tr key={service._id}>
                      <td>{index + 1 + (currentPage - 1) * servicesPerPage}</td>
                      {editingService && editingService._id === service._id ? (
                        <>
                          <td>
                            <Form.Control
                              type="text"
                              name="providerType"
                              value={editingService.providerType}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              name="service"
                              value={editingService.service}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Button variant="success" onClick={handleSubmit} className="me-2">Save</Button>
                            <Button variant="secondary" onClick={() => setEditingService(null)}>Cancel</Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{service.providerType}</td>
                          <td>{service.service}</td>
                          <td className="d-flex align-items-center">
                            <Button variant="info" className="me-2" onClick={() => handleEditService(service)}>
                              <BsPencilSquare />
                            </Button>
                            <Button variant="danger" onClick={() => handleDeleteService(service.serviceId)}>
                              <BsTrash />
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                {[...Array(Math.ceil(sortedAndFilteredServices.length / servicesPerPage)).keys()].map(number => (
                  <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => paginate(number + 1)}>
                    {number + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Service Modal */}
      <Modal show={showAddServiceModal} onHide={() => setShowAddServiceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddServiceSubmit}>
            <Form.Group controlId="formProviderType">
              <Form.Label>Provider Type</Form.Label>
              <Form.Control as="select" name="providerType" value={newService.providerType} onChange={handleAddServiceChange}>
                <option value="Doctor">Doctor</option>
                <option value="Trainer">Trainer</option>
                <option value="Laboratory">Laboratory</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="formService">
              <Form.Label>Service</Form.Label>
              <Form.Control
                type="text"
                name="service"
                value={newService.service}
                onChange={handleAddServiceChange}
                placeholder="Enter service"
                required
              />
            </Form.Group>
            <Button variant="primary" type="submit" className="mt-3">
              Add Service
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Services;
